import { Component, Input, OnChanges, SimpleChanges, signal, WritableSignal, inject, EventEmitter, Output, OnInit } from '@angular/core';
import moment from 'moment';
import { EmployeeInfoService } from '../../../core/services/employee-info.service';
import { EmployeeService } from '../../../pages/employee/services/employee.service';
import { ngfModule } from 'angular-file';
import { FileUtilityService } from '../../../core/services/utilities/file-utility.service';
import { FileData } from '../../../core/models/file-data';
import { mergeAll, mergeMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { NgIf } from '@angular/common';
import { ApiResponse } from '../../model/api-response.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProgressBarModalComponent } from '../../../modals/shared/progress-bar-modal/progress-bar-modal.component';
import { ConfirmationModalComponent } from '../../../modals/home/confirmation-modal/confirmation-modal.component';
import { NotificationsService } from '../../../core/services/notifications.service';
import { UtilityService } from '../../../core/services/utility.service';
import { ReceiptIDs } from '../../model/receipt-ids.model';
import { BACKDROP_STATIC, SIZE_LARGE, SIZE_SMALL } from '../../constants/modal.constant';
import { PortalSettingsService } from '../../../core/services/portal-settings.service';

@Component({
  selector: 'app-expense-quick-add-button',
  standalone: true,
  imports: [NgIf, ngfModule],
  templateUrl: './expense-quick-add-button.component.html',
  styleUrl: './expense-quick-add-button.component.scss',
})
export class ExpenseQuickAddButtonComponent implements OnChanges {
  private readonly employeeInfo = inject(EmployeeInfoService).getEmployeeInfo();
  private readonly employeeService = inject(EmployeeService);
  private readonly fileUtilityService = inject(FileUtilityService);
  private readonly modalService = inject(NgbModal);
  private readonly notificationService = inject(NotificationsService);
  private readonly portalSettings = inject(PortalSettingsService).getPortalSettings();
  readonly currencySymbol = inject(UtilityService).getCurrencySymbol(this.employeeInfo.PRCo);

  @Input() buttonType: 'mobile-nav' | 'desktop-expense' | 'mobile-expense' = 'desktop-expense';
  @Input() userKeyID: number;
  @Output() expenseAdded = new EventEmitter<void>();

  uploadedFiles: File[];

  // Signals for progress bar:
  uploadProgress = signal(0);
  totalFilesToUpload = signal(0);
  progressText: WritableSignal<string> = signal(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userKeyID && !changes.userKeyID.isFirstChange()) {
      this.userKeyID = changes.userKeyID.currentValue;
    }
  }

  async quickAddExpense(): Promise<void> {
    this.openProgressBar();
    this.progressText.set('Creating Expense');
    const newReceiptIDs = await this.createNewExpense();
    if (!newReceiptIDs) {
      this.closeProgressBar();
    }
    this.uploadAttachments(newReceiptIDs);
  }

  async createNewExpense(): Promise<ReceiptIDs> {
    const today = moment().format('YYYY-MM-DD');
    const description = this.generateExpenseDescription();

    try {
      const newExpense = await this.employeeService.insertUpdateReceipt(-1, this.userKeyID, this.employeeInfo.PRCo, description, today);
      const newExpenseItemID = await this.employeeService.insertUpdateReceiptItem(-1, newExpense.ReceiptID);
      return { ID: newExpense?.ReceiptID, ItemID: newExpenseItemID };
    } catch (error) {
      this.notificationService.showError(
        error?.error?.error ?? 'There was an error creating the expense. Please try again later.',
        window.location.pathname
      );
      return null;
    }
  }

  generateExpenseDescription(): string {
    const today = new Date();
    return `Quick Add at ${today.toLocaleTimeString()}`;
  }

  uploadAttachments(newReceiptIDs: ReceiptIDs): void {
    if (!newReceiptIDs) return;
    this.fileUtilityService
      .getFileDataForFiles(this.uploadedFiles)
      .pipe(
        tap((fileData: FileData[]) => this.totalFilesToUpload.set(fileData.length)),
        mergeMap((fileData: FileData[]) => this.addAttachments(fileData, newReceiptIDs.ID, newReceiptIDs.ItemID)),
        mergeAll()
      )
      .subscribe({
        next: () => {
          this.uploadProgress.update(value => ++value);
        },
        error: error => {
          this.notificationService.showError(`Error uploading files: ${error?.error?.error}`, window.location.pathname);
          this.closeProgressBar();
        },
        complete: () => {
          this.openExpenseModal(newReceiptIDs.ID, newReceiptIDs.ItemID, this.userKeyID);
          this.resetFileUploadObjects();
        },
      });
  }

  addAttachments(files: FileData[], receiptID: number, receiptItemID: number): Observable<ApiResponse>[] {
    const addAttachmentToReceiptHeader = this.portalSettings?.EnableAddAttachmentToReceipt?.ConfigValue === '1';
    return files.map((file: FileData) => {
      return this.employeeService
        .addAttachment(receiptID, addAttachmentToReceiptHeader ? null : receiptItemID, file.data, file.name)
        .pipe(tap(() => this.progressText.set(`Uploaded ${file.name}`)));
    });
  }

  openProgressBar(): void {
    this.resetProgressBar();
    const modalRef = this.modalService.open(ProgressBarModalComponent, { size: SIZE_SMALL, backdrop: BACKDROP_STATIC });
    modalRef.componentInstance.progress = this.uploadProgress;
    modalRef.componentInstance.totalTasks = this.totalFilesToUpload;
    modalRef.componentInstance.progressText = this.progressText;
  }

  closeProgressBar(): void {
    this.modalService.dismissAll();
  }

  resetProgressBar(): void {
    this.uploadProgress.set(0);
    this.progressText.set(null);
  }

  openExpenseModal(receiptID: number, receiptItemID: number, userKeyID: number): void {
    const expenseData = {
      newID: receiptID,
      receiptItemID: receiptItemID,
      userKeyID: userKeyID,
    };

    const modalRef = this.modalService.open(ConfirmationModalComponent, { size: SIZE_LARGE, backdrop: BACKDROP_STATIC });
    modalRef.componentInstance.data = expenseData;
    modalRef.result.then(() => this.expenseAdded.emit()).catch(() => {});
  }

  resetFileUploadObjects(): void {
    this.uploadedFiles = null;
  }
}
